import React from 'react'
import { TimelineSpan } from '../../domain/TimelineSpan'
import { getTraceSpanCategory } from '../../domain/Trace'

interface Props {
    spans: TimelineSpan[]
    allSpans?: TimelineSpan[]
    currentPage: number
    totalPages: number
    traceDuration?: number
    onPageChange?: (page: number) => void
}

export const SpanPaginationControls = ({ spans, allSpans, currentPage, totalPages, traceDuration, onPageChange }: Props) => {
    if (totalPages <= 1) {
        return null
    }

    const pageCounterWidth = `${String(totalPages).length * 2 + 1}ch`
    const chartWidth = 240
    const chartHeight = 22
    const triangleHeight = 5
    const barY = triangleHeight + 3
    const barHeight = 8
    const selectedSpan = spans[currentPage - 1]
    const selectedSpanCategoryClassName = selectedSpan ? 'category-' + getTraceSpanCategory(selectedSpan).className : null
    const timelineSpans = allSpans && allSpans.length > 0 ? allSpans : spans
    const maxEnd = Math.max(...timelineSpans.map(span => span.cs + span.wt))
    // Use the full trace time domain so gaps between repeated spans stay visually to scale.
    const duration = Math.max(1, traceDuration || 0, maxEnd)
    const selectedMarkerX = selectedSpan ? ((selectedSpan.cs + selectedSpan.wt / 2) / duration) * chartWidth : null
    const selectedMarkerPoints =
        selectedMarkerX !== null ? `${selectedMarkerX - 4},0 ${selectedMarkerX + 4},0 ${selectedMarkerX},${triangleHeight}` : null

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
            <div style={{ overflow: 'hidden', paddingTop: '1px', flex: '1 1 auto', minWidth: 0 }}>
                <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none">
                    {spans.map((span, index) => {
                        const x = (span.cs / duration) * chartWidth
                        const width = Math.max(2, (span.wt / duration) * chartWidth)
                        const isSelectedSpan = span.id === selectedSpan?.id
                        const spanCategoryClassName = 'category-' + getTraceSpanCategory(span).className

                        return (
                            <rect
                                key={span.id}
                                x={x}
                                y={barY}
                                width={Math.min(chartWidth - x, width)}
                                height={barHeight}
                                rx={2}
                                ry={2}
                                className={spanCategoryClassName}
                                fillOpacity={isSelectedSpan ? 1 : 0.35}
                                style={onPageChange ? { cursor: 'pointer' } : undefined}
                                onClick={onPageChange ? () => onPageChange(index + 1) : undefined}
                            />
                        )
                    })}
                    {selectedSpan ? (
                        <polygon points={selectedMarkerPoints || undefined} className={selectedSpanCategoryClassName || undefined} />
                    ) : null}
                </svg>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                <small
                    className="text-muted"
                    style={{ display: 'inline-block', width: pageCounterWidth, textAlign: 'right', whiteSpace: 'nowrap' }}
                >
                    {currentPage}/{totalPages}
                </small>
                <div className="btn-group" style={{ marginLeft: '8px' }}>
                    <button
                        type="button"
                        className="btn btn-default btn-xs"
                        disabled={currentPage <= 1 || !onPageChange}
                        aria-label="Previous span"
                        onClick={currentPage > 1 && onPageChange ? () => onPageChange(currentPage - 1) : undefined}
                    >
                        <i className="fa fa-chevron-left" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-default btn-xs"
                        disabled={currentPage >= totalPages || !onPageChange}
                        aria-label="Next span"
                        onClick={currentPage < totalPages && onPageChange ? () => onPageChange(currentPage + 1) : undefined}
                    >
                        <i className="fa fa-chevron-right" />
                    </button>
                </div>
            </div>
        </div>
    )
}
